import { ColWidth } from '../models/shared/col-width';
import { ReportRestockedProductsSection } from '../models/menu/section/report/report-restocked-products-section';
import { StringUtils } from './string-utils';
import { SectionColumnConfigState } from '../models/enum/shared/section-column-config-state';
import { SectionColumnConfigProductInfoKey } from '../models/enum/shared/section-column-config-product-info-key';
import { SectionColumnConfigCannabinoidKey } from '../models/enum/shared/section-column-config-cannabinoid-key';
import { SectionColumnConfigTerpeneKey } from '../models/enum/shared/section-column-config-terpene-key';
import { SectionColumnViewModel } from '../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import { SectionColumnCannabinoidType } from '../models/enum/shared/section-column-cannabinoid-type';
import { SectionColumnTerpeneType } from '../models/enum/shared/section-column-terpene-type';
import { SectionColumnProductInfoType } from '../models/enum/shared/section-column-product-info-type';
import type { ColumnGrouping } from '../models/menu/section/column-grouping';
import type { SectionLayoutType } from '../models/enum/dto/section-layout-type.enum';
import type { SectionColumnConfigKey } from '../models/enum/shared/section-column-config-key';
import type { SectionColumnConfig } from '../models/menu/section/section-column-config';
import type { SectionColumnType } from '../models/enum/shared/section-column-type';
import type { SectionRowViewModel } from '../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import type { ProductMenu } from '../models/menu/product-menu';
import { exists } from '../functions/exists';
import { isProductTagGroupKey, SectionColumnTagGroupType } from '../models/enum/shared/section-theme-tag-group-keys';

// @dynamic
export class ColumnViewModelUtils {
  /* *************************** Standardized Column View Models *************************** */

  static standardizedDigitalColumnViewModels(
    menu: ProductMenu,
    sectionRowVMs: SectionRowViewModel[],
    rowVM: SectionRowViewModel,
    widths: ColWidth[]
  ): SectionColumnViewModel[] {
    const configs = rowVM?.section?.columnConfig;
    const order = menu?.getColumnOrdering();
    const colVMs: SectionColumnViewModel[] = [];
    const layoutType = rowVM?.getLayoutType();
    ColumnViewModelUtils.defaultProductTitleColumnAdder(layoutType, order, colVMs);
    ColumnViewModelUtils.defaultBrandColumnAdder(sectionRowVMs, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultStrainColumnAdder(menu, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultStrainClassColumnAdder(menu, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultLineageColumnAdder(menu, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultAssetColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultBadgeColumnAdder(sectionRowVMs, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultHelpsWithColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultEffectsColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultAromaColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultGrowingConditionsColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultCraftDesignationColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultAppearanceColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultFlavorColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultTopFlavorColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultEnergyScoreColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultPrimaryCannabinoidColAdder(sectionRowVMs, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultSecondaryCannabinoidColAdder(sectionRowVMs, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultTerpeneColAdder(sectionRowVMs, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultProductTagGroupColAdder(rowVM, configs, layoutType, order, widths, colVMs);
    ColumnViewModelUtils.defaultVariantTypeColumnAdder(configs, layoutType, order, widths, colVMs);
    if (rowVM?.section?.isInLineMode()) {
      ColumnViewModelUtils.defaultStockColumnAdder(configs, layoutType, order, widths, colVMs);
      ColumnViewModelUtils.defaultQuantityAndSizeColumnAdder(configs, layoutType, order, widths, colVMs);
      ColumnViewModelUtils.defaultQuantityColumnAdder(configs, layoutType, order, widths, rowVM, colVMs);
      ColumnViewModelUtils.defaultSizeColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
      ColumnViewModelUtils.defaultGradingResultsColumnAdder(rowVM, configs, layoutType, order, widths, colVMs);
      ColumnViewModelUtils.defaultPriceColumnAdder(configs, layoutType, order, widths, colVMs);
    } else {
      const vms = rowVM?.section?.generateGridLayoutColumnViewModels(menu, widths) || [];
      colVMs.push(...vms);
    }
    ColumnViewModelUtils.defaultSecondaryPriceColumnAdder(configs, layoutType, order, widths, colVMs);
    return colVMs;
  }

  static defaultProductTitleColumnAdder(
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    colVMs: SectionColumnViewModel[]
  ): void {
    colVMs.push(SectionColumnViewModel.getProductTitleColumn(layoutType, order));
  }

  static defaultBrandColumnAdder(
    sectionRowViewModels: SectionRowViewModel[],
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Brand);
    if (isFixedLocation) return;
    const brandColumnState = configs?.get(SectionColumnConfigProductInfoKey.Brand)?.defaultState;
    const brandOn = brandColumnState === SectionColumnConfigState.On;
    const hasSomeBrands = sectionRowViewModels?.some(vm => vm?.hasBrands());
    const brandAuto = brandColumnState === SectionColumnConfigState.Auto && hasSomeBrands;
    if (brandOn || brandAuto) {
      colVMs.push(SectionColumnViewModel.getBrandColumn(configs, layoutType, order, widths));
    }
  }

  static defaultStrainColumnAdder(
    menu: ProductMenu,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Strain);
    if (isFixedLocation) return;
    const strainColumnState = configs?.get(SectionColumnConfigProductInfoKey.Strain)?.defaultState;
    const strainOn = strainColumnState === SectionColumnConfigState.On;
    if (strainOn) {
      colVMs.push(SectionColumnViewModel.getStrainColumn(configs, layoutType, order, widths));
    }
  }

  static defaultStrainClassColumnAdder(
    menu: ProductMenu,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.StrainType);
    if (isFixedLocation) return;
    const strainClassColumnState = configs?.get(SectionColumnConfigProductInfoKey.StrainType)?.defaultState;
    const strainClassOn = strainClassColumnState === SectionColumnConfigState.On;
    const showStrainClassInAssetColumn = menu?.getShowClassificationsInAssetColumn();
    if (strainClassOn && !showStrainClassInAssetColumn) {
      colVMs.push(SectionColumnViewModel.getStrainClassColumn(configs, layoutType, order, widths));
    }
  }

  static defaultLineageColumnAdder(
    menu: ProductMenu,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Lineage);
    if (isFixedLocation) return;
    const lineageColumnState = configs?.get(SectionColumnConfigProductInfoKey.Lineage)?.defaultState;
    const lineageOn = lineageColumnState === SectionColumnConfigState.On;
    if (lineageOn) {
      colVMs.push(SectionColumnViewModel.getLineageColumn(configs, layoutType, order, widths));
    }
  }

  static defaultAssetColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Asset);
    if (isFixedLocation) return;
    const isRestockedSection = rowViewModel?.section instanceof ReportRestockedProductsSection;
    const assetColumnState = configs?.get(SectionColumnConfigProductInfoKey.Asset)?.defaultState;
    const assetsOn = assetColumnState === SectionColumnConfigState.On;
    if (assetsOn && !isRestockedSection) {
      colVMs.push(SectionColumnViewModel.getAssetColumn(configs, layoutType, order, widths));
    }
  }

  static defaultHelpsWithColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.HelpsWith);
    if (isFixedLocation) return;
    const columnState = configs?.get(SectionColumnConfigProductInfoKey.HelpsWith)?.defaultState;
    const on = columnState === SectionColumnConfigState.On;
    if (on) {
      colVMs.push(SectionColumnViewModel.getHelpsWithColumn(configs, layoutType, order, widths));
    }
  }

  static defaultAromaColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Aroma);
    if (isFixedLocation) return;
    const columnState = configs?.get(SectionColumnConfigProductInfoKey.Aroma)?.defaultState;
    const on = columnState === SectionColumnConfigState.On;
    if (on) {
      colVMs.push(SectionColumnViewModel.getAromaColumn(configs, layoutType, order, widths));
    }
  }

  static defaultGrowingConditionsColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(
      configs,
      SectionColumnConfigProductInfoKey.GrowingConditions
    );
    if (isFixedLocation) return;
    const columnState = configs?.get(SectionColumnConfigProductInfoKey.GrowingConditions)?.defaultState;
    const on = columnState === SectionColumnConfigState.On;
    if (on) {
      colVMs.push(SectionColumnViewModel.getGrowingConditionsColumn(configs, layoutType, order, widths));
    }
  }
  static defaultCraftDesignationColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(
      configs,
      SectionColumnConfigProductInfoKey.CraftDesignation
    );
    if (isFixedLocation) return;
    const columnState = configs?.get(SectionColumnConfigProductInfoKey.CraftDesignation)?.defaultState;
    const on = columnState === SectionColumnConfigState.On;
    if (on) {
      colVMs.push(SectionColumnViewModel.getCraftDesignationColumn(configs, layoutType, order, widths));
    }
  }
  static defaultAppearanceColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Appearance);
    if (isFixedLocation) return;
    const columnState = configs?.get(SectionColumnConfigProductInfoKey.Appearance)?.defaultState;
    const on = columnState === SectionColumnConfigState.On;
    if (on) {
      colVMs.push(SectionColumnViewModel.getAppearanceColumn(configs, layoutType, order, widths));
    }
  }
  static defaultFlavorColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Flavor);
    if (isFixedLocation) return;
    const columnState = configs?.get(SectionColumnConfigProductInfoKey.Flavor)?.defaultState;
    const on = columnState === SectionColumnConfigState.On;
    if (on) {
      colVMs.push(SectionColumnViewModel.getFlavorColumn(configs, layoutType, order, widths));
    }
  }
  static defaultTopFlavorColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.TopFlavor);
    if (isFixedLocation) return;
    const columnState = configs?.get(SectionColumnConfigProductInfoKey.TopFlavor)?.defaultState;
    const on = columnState === SectionColumnConfigState.On;
    if (on) {
      colVMs.push(SectionColumnViewModel.getTopFlavorColumn(configs, layoutType, order, widths));
    }
  }
  static defaultEnergyScoreColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(
      configs,
      SectionColumnConfigProductInfoKey.EnergyScore
    );
    if (isFixedLocation) return;
    const columnState = configs?.get(SectionColumnConfigProductInfoKey.EnergyScore)?.defaultState;
    const on = columnState === SectionColumnConfigState.On;
    if (on) {
      colVMs.push(SectionColumnViewModel.getEnergyScoreColumn(configs, layoutType, order, widths));
    }
  }
  static defaultEffectsColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Effects);
    if (isFixedLocation) return;
    const columnState = configs?.get(SectionColumnConfigProductInfoKey.Effects)?.defaultState;
    const on = columnState === SectionColumnConfigState.On;
    if (on) {
      colVMs.push(SectionColumnViewModel.getEffectsColumn(configs, layoutType, order, widths));
    }
  }

  static defaultBadgeColumnAdder(
    sectionRowViewModels: SectionRowViewModel[],
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Badges);
    if (isFixedLocation) return;
    const badgeColumnState = configs?.get(SectionColumnConfigProductInfoKey.Badges)?.defaultState;
    const badgesOn = badgeColumnState === SectionColumnConfigState.On;
    const hasSomeBadges = sectionRowViewModels?.some(vm => vm?.hasBadges());
    const badgesAuto = badgeColumnState === SectionColumnConfigState.Auto && hasSomeBadges;
    const noBadgeColumnDataFallBackToAuto = !badgeColumnState && hasSomeBadges;
    if (badgesOn || badgesAuto || noBadgeColumnDataFallBackToAuto) {
      colVMs.push(SectionColumnViewModel.getBadgeColumn(configs, layoutType, order, widths));
    }
  }

  static defaultPrimaryCannabinoidColAdder(
    secRowVMs: SectionRowViewModel[],
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const columnAdder = (
      configKey: keyof typeof SectionColumnConfigCannabinoidKey,
      type: keyof typeof SectionColumnCannabinoidType
    ) => {
      const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, configKey);
      if (isFixedLocation) return;
      const columnState = configs?.get(configKey)?.defaultState;
      const on = columnState === SectionColumnConfigState.On;
      const autoActive = ColumnViewModelUtils.cannabinoidAutoColumnEnabled(secRowVMs, type);
      const auto = columnState === SectionColumnConfigState.Auto && autoActive;
      const noColumnDataFallBackToAuto = !columnState;
      if (on || auto || noColumnDataFallBackToAuto) {
        colVMs.push(SectionColumnViewModel.getCannabinoidColumn(configs, layoutType, order, widths, configKey, type));
      }
    };
    columnAdder(SectionColumnConfigCannabinoidKey.THC, SectionColumnCannabinoidType.THC);
    columnAdder(SectionColumnConfigCannabinoidKey.CBD, SectionColumnCannabinoidType.CBD);
    columnAdder(SectionColumnConfigCannabinoidKey.TAC, SectionColumnCannabinoidType.TAC);
  }

  static defaultSecondaryCannabinoidColAdder(
    sectionRowViewModels: SectionRowViewModel[],
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const columnAdder = (
      configKey: keyof typeof SectionColumnConfigCannabinoidKey,
      type: keyof typeof SectionColumnCannabinoidType
    ) => {
      const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, configKey);
      if (isFixedLocation) return;
      const columnState = configs?.get(configKey)?.defaultState;
      const on = columnState === SectionColumnConfigState.On;
      const autoActive = ColumnViewModelUtils.cannabinoidAutoColumnEnabled(sectionRowViewModels, type);
      const auto = columnState === SectionColumnConfigState.Auto && autoActive;
      if (on || auto) {
        colVMs.push(SectionColumnViewModel.getCannabinoidColumn(configs, layoutType, order, widths, configKey, type));
      }
    };
    Object.values(SectionColumnConfigCannabinoidKey)?.forEach(key => {
      const thc = key === SectionColumnConfigCannabinoidKey.THC;
      const cbd = key === SectionColumnConfigCannabinoidKey.CBD;
      const tac = key === SectionColumnConfigCannabinoidKey.TAC;
      if (!thc && !cbd && !tac) {
        const type = SectionColumnCannabinoidType[key];
        columnAdder(key, type);
      }
    });
  }

  static defaultTerpeneColAdder(
    sectionRowViewModels: SectionRowViewModel[],
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const columnAdder = (configKey: SectionColumnConfigKey, type: SectionColumnType) => {
      const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, configKey);
      if (isFixedLocation) return;
      const columnState = configs?.get(configKey)?.defaultState;
      const on = columnState === SectionColumnConfigState.On;
      const autoActive = ColumnViewModelUtils.terpeneAutoColumnEnabled(sectionRowViewModels, type);
      const auto = columnState === SectionColumnConfigState.Auto && autoActive;
      if (on || auto) {
        colVMs.push(SectionColumnViewModel.getTerpeneColumn(configs, layoutType, order, widths, configKey, type));
      }
    };
    Object.values(SectionColumnConfigTerpeneKey)?.forEach(key => {
      const type = SectionColumnTerpeneType[key];
      columnAdder(key, type);
    });
  }

  static defaultProductTagGroupColAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const columnAdder = (configKey: SectionColumnConfigKey, type: SectionColumnTagGroupType) => {
      const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, configKey);
      if (isFixedLocation) return;
      const columnState = configs?.get(configKey)?.defaultState;
      const menu = rowViewModel?.menu;
      const locationPriceStream = rowViewModel?.locationConfig?.priceFormat;
      const on = columnState === SectionColumnConfigState.On;
      const auto =
        columnState === SectionColumnConfigState.Auto &&
        rowViewModel?.section?.showProductTagGroupColumn(
          menu,
          locationPriceStream,
          type,
          rowViewModel?.companyConfig?.enabledCuratedTagGroupIds,
          rowViewModel?.companyConfig?.enabledCuratedTagIds
        );
      if (on || auto) {
        colVMs.push(
          SectionColumnViewModel.getProductTagGroupColumn(configs, layoutType, order, widths, configKey, type)
        );
      }
    };
    const productTagGroupKeys = Array.from(configs.keys()).filter(key => isProductTagGroupKey(key));
    // This isn't a bug. They keys and types match 1:1 for tag groups.
    // There is no way to generate the type from the key other than a direct cast.
    productTagGroupKeys?.forEach(key => columnAdder(key, key as SectionColumnTagGroupType));
  }

  static defaultVariantTypeColumnAdder(
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(
      configs,
      SectionColumnConfigProductInfoKey.VariantType
    );
    if (isFixedLocation) return;
    const variantTypeColumnState = configs?.get(SectionColumnConfigProductInfoKey.VariantType)?.defaultState;
    const variantTypeOn = variantTypeColumnState === SectionColumnConfigState.On;
    if (variantTypeOn) {
      colVMs.push(SectionColumnViewModel.getVariantTypeColumn(configs, layoutType, order, widths));
    }
  }

  static defaultStockColumnAdder(
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Stock);
    if (isFixedLocation) return;
    const qtyInStockColumnState = configs?.get(SectionColumnConfigProductInfoKey.Stock)?.defaultState;
    const qtyInStockOn = qtyInStockColumnState === SectionColumnConfigState.On;
    if (qtyInStockOn) {
      colVMs.push(SectionColumnViewModel.getStockColumn(configs, layoutType, order, widths));
    }
  }

  static defaultQuantityAndSizeColumnAdder(
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(
      configs,
      SectionColumnConfigProductInfoKey.QuantityAndSize
    );
    if (isFixedLocation) return;
    const qtyAndSizeColumnState = configs?.get(SectionColumnConfigProductInfoKey.QuantityAndSize)?.defaultState;
    const qtyAndSizeOn = qtyAndSizeColumnState === SectionColumnConfigState.On;
    const qtyAndSizeAuto = qtyAndSizeColumnState === SectionColumnConfigState.Auto;
    const noQtyAndSizeColumnDataFallBackToAuto = !qtyAndSizeColumnState;
    if (qtyAndSizeOn || qtyAndSizeAuto || noQtyAndSizeColumnDataFallBackToAuto) {
      colVMs.push(SectionColumnViewModel.getQuantityAndSizeColumn(configs, layoutType, order, widths));
    }
  }

  /**
   * Auto state for quantity column looks at ALL variants before the section was split by the overflow calculator.
   */
  static defaultQuantityColumnAdder(
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    rowViewModel: SectionRowViewModel,
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Quantity);
    if (isFixedLocation) return;
    const quantityColumnState = configs?.get(SectionColumnConfigProductInfoKey.Quantity)?.defaultState;
    const quantityOn = quantityColumnState === SectionColumnConfigState.On;
    const menu = rowViewModel?.menu;
    const locationPriceStream = rowViewModel?.locationConfig?.priceFormat;
    const sectionShowQuantityColumnSwitch = rowViewModel?.section?.showQuantityColumn(menu, locationPriceStream);
    const quantityAuto = quantityColumnState === SectionColumnConfigState.Auto && sectionShowQuantityColumnSwitch;
    const noQuantityColumnDataFallBackToAuto = !quantityColumnState && sectionShowQuantityColumnSwitch;
    if (quantityOn || quantityAuto || noQuantityColumnDataFallBackToAuto) {
      colVMs.push(SectionColumnViewModel.getQuantityColumn(configs, layoutType, order, widths));
    }
  }

  /**
   * Auto state for size column looks at ALL variants before the section was split by the overflow calculator.
   */
  static defaultSizeColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Size);
    if (isFixedLocation) return;
    const sizeColumnState = configs?.get(SectionColumnConfigProductInfoKey.Size)?.defaultState;
    const sizeOn = sizeColumnState === SectionColumnConfigState.On;
    const menu = rowViewModel?.menu;
    const locationPriceStream = rowViewModel?.locationConfig?.priceFormat;
    const sectionShowSizeColumn = rowViewModel?.section?.showSizeColumn(menu, locationPriceStream);
    const sizeAuto = sizeColumnState === SectionColumnConfigState.Auto && sectionShowSizeColumn;
    const noSizeColumnDataFallBackToAuto = !sizeColumnState && sectionShowSizeColumn;
    if (sizeOn || sizeAuto || noSizeColumnDataFallBackToAuto) {
      colVMs.push(SectionColumnViewModel.getSizeColumn(configs, layoutType, order, widths));
    }
  }

  static defaultGradingResultsColumnAdder(
    rowViewModel: SectionRowViewModel,
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(
      configs,
      SectionColumnConfigProductInfoKey.GradingResults
    );
    if (isFixedLocation) return;
    const config = configs?.get(SectionColumnConfigProductInfoKey.GradingResults);
    const gradingResultsColumnState = config?.defaultState;
    const gradingProvider = config?.dataValue;
    const gradingResultsOn = gradingResultsColumnState === SectionColumnConfigState.On;
    const menu = rowViewModel?.menu;
    const locationPriceStream = rowViewModel?.locationConfig?.priceFormat;
    const showGradingResultsCol = rowViewModel?.section?.showGradingResultsColumn(
      menu,
      locationPriceStream,
      gradingProvider
    );
    const gradingResultsAuto = gradingResultsColumnState === SectionColumnConfigState.Auto && showGradingResultsCol;
    if (gradingResultsOn || gradingResultsAuto) {
      colVMs.push(SectionColumnViewModel.getReviewResultsColumn(configs, layoutType, order, widths));
    }
  }

  static defaultPriceColumnAdder(
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(configs, SectionColumnConfigProductInfoKey.Price);
    if (isFixedLocation) return;
    const priceColumnState = configs?.get(SectionColumnConfigProductInfoKey.Price)?.defaultState;
    const priceOn = priceColumnState === SectionColumnConfigState.On;
    const priceAuto = priceColumnState === SectionColumnConfigState.Auto;
    const noPriceColumnDataFallBackToAuto = !priceColumnState;
    if (priceOn || priceAuto || noPriceColumnDataFallBackToAuto) {
      colVMs.push(SectionColumnViewModel.getPriceColumn(configs, layoutType, order, widths));
    }
  }

  static defaultSecondaryPriceColumnAdder(
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    layoutType: SectionLayoutType,
    order: Map<SectionColumnType, number>,
    widths: ColWidth[],
    colVMs: SectionColumnViewModel[]
  ): void {
    const isFixedLocation = ColumnViewModelUtils.isFixedLocation(
      configs,
      SectionColumnConfigProductInfoKey.SecondaryPrice
    );
    if (isFixedLocation) return;
    const secondaryPriceColumnState = configs?.get(SectionColumnConfigProductInfoKey.SecondaryPrice)?.defaultState;
    const secondaryPriceOn = secondaryPriceColumnState === SectionColumnConfigState.On;
    const secondaryPriceAuto = secondaryPriceColumnState === SectionColumnConfigState.Auto;
    if (secondaryPriceOn || secondaryPriceAuto) {
      colVMs.push(SectionColumnViewModel.getSecondaryPriceColumn(configs, layoutType, order, widths));
    }
  }

  static standardizedPrintColumnViewModels(
    menu: ProductMenu,
    sectionRowViewModels: SectionRowViewModel[],
    rowViewModel: SectionRowViewModel,
    widths: ColWidth[]
  ): SectionColumnViewModel[] {
    return ColumnViewModelUtils.standardizedDigitalColumnViewModels(menu, sectionRowViewModels, rowViewModel, widths);
  }

  /* *********************************************************************************** */

  static addSpacersBetweenGroupings(
    spacerWidth: string,
    groupings: ColumnGrouping[],
    columnViewModels: SectionColumnViewModel[]
  ): SectionColumnViewModel[] {
    groupings?.forEach(grouping => grouping.setIfGroupingExists(columnViewModels));
    const existingGroups = groupings?.filter(grouping => grouping.exists);
    if (existingGroups?.length > 1) {
      const spacerCol = new SectionColumnViewModel(null, null);
      spacerCol.columnType = SectionColumnProductInfoType.Spacer;
      spacerCol.widthPercentage = spacerWidth;
      existingGroups.pop();
      existingGroups?.forEach(grouping => {
        const indices = grouping?.columns
          ?.map(groupColumn => {
            return columnViewModels?.map(columnViewModel => columnViewModel?.columnType)?.lastIndexOf(groupColumn);
          })
          ?.filter(index => index > -1);
        const insertAt = Math.max(...indices) + 1;
        if (isFinite(insertAt)) {
          columnViewModels?.splice(insertAt, 0, spacerCol);
        }
      });
    }
    return columnViewModels;
  }

  static cannabinoidAutoColumnEnabled(
    sectionRowViewModels: SectionRowViewModel[],
    columnType: keyof typeof SectionColumnCannabinoidType
  ): boolean {
    const cannabinoid = sectionRowViewModels.map(vm => {
      return columnType === SectionColumnCannabinoidType.TAC
        ? vm.getMinRowTotalActiveCannabinoids()
        : vm.getMinRowCannabinoid(columnType);
    });
    const maxVisibleCannabinoidInColumn = Math.max(...cannabinoid);
    const thc = columnType === SectionColumnCannabinoidType.THC;
    const cbd = columnType === SectionColumnCannabinoidType.CBD;
    const thcAndCbd = columnType === SectionColumnCannabinoidType.THCAndCBD;
    if (thc || cbd || thcAndCbd) {
      return maxVisibleCannabinoidInColumn >= 1.0;
    }
    return maxVisibleCannabinoidInColumn > 0;
  }

  static terpeneAutoColumnEnabled(sectionRowViewModels: SectionRowViewModel[], columnType: SectionColumnType): boolean {
    switch (columnType) {
      case SectionColumnTerpeneType.TopTerpene:
        return sectionRowViewModels.some(vm => {
          const topTerpene = vm.getTopTerpene();
          return exists(topTerpene) && topTerpene !== '-';
        });
      case SectionColumnTerpeneType.TotalTerpene:
        return sectionRowViewModels.some(vm => vm.getMinRowTotalActiveTerpenes() > 0);
      default:
        const terpeneCamelCased = StringUtils.pascalCaseToCamelCase(columnType);
        return sectionRowViewModels.some(vm => vm.getMinRowTerpene(terpeneCamelCased) > 0);
    }
  }

  static isFixedLocation(
    configs: Map<SectionColumnConfigKey, SectionColumnConfig | null>,
    key: SectionColumnConfigKey
  ): boolean {
    return configs?.get(key)?.isFixedLocation === true;
  }
}
